import { action, observable } from 'mobx';
import stores from '../../../models/Stores';
import MapImageLayer from '@arcgis/core/layers/MapImageLayer';
import { FeatureSet } from 'cd-eft/utils/esriTypes';
import { MapView } from 'cd-eft/utils/esriTypes';
import { resetMap } from 'cd-eft/components/SurveyEngineering/Maps/SETMapFunctions/mapOperations';
import { DesignContainerVM } from 'cd-eft/components/Design/DesignContainer/DesignContainerVM';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import * as SETAPI from 'cd-eft/services/eftSetApi';
import { DesignElementResponse } from 'cd-eft/_codegen_/openapi-generator/eft-set';
import { appConfig } from 'cd-eft/config/AppConfig';
import { EngineeringMapService } from 'cd-eft/framework/mapServices/EngineeringMapServices';
import { INotification } from 'cd-eft/models/Stores/DesignStore';

const { padDesignStore, designStore, projectsLibraryStore } = stores.AppStore;
const setStore = stores.AppStore.surveyEngineeringStore;
const gisModel = stores.AppStore.surveyEngineeringStore.gisModel;

export class PadSummaryVM {
  @observable props;
  @observable isLoading: boolean = false;
  @observable jobExecuting: boolean = false;
  @observable jobMessage: string = '';
  @observable mapServiceLayer: any = MapImageLayer;
  @observable output_ground_points: any = FeatureSet;
  @observable notification: INotification = {
    message: '',
    header: '',
    type: '',
    hidden: true,
    timestamp: undefined,
  };
  @observable error = false;
  @observable errorMsg = '';
  @observable selectDisplayPads = false;
  @observable selectSurface = false;
  @observable displayType = 'none';
  @observable somePadSelected = false;
  @observable selectedPadsInfo: any = [];

  private mapView?: MapView;
  constructor(props: any) {
    this.props = props;
    if (props.mapView) {
      this.mapView = props.mapView;
    }
  }

  @observable elementActions = {
    Edit: { action: this.openSinglePad },
    Delete: { action: this.deleteDesignElement },
  };

  @action.bound clearNotification(): void {
    this.notification = { message: '', header: '', type: '', hidden: true, timestamp: undefined };
  }

  @action.bound statusCallback(jobInfo: __esri.JobInfo) {
    let messageIndex = 0;
    if (jobInfo && jobInfo.messages && jobInfo.messages.length > 0) {
      if (jobInfo.messages) {
        if (jobInfo.messages.length > messageIndex)
          for (let i = messageIndex; i < jobInfo.messages.length; i++) {
            // console.log(jobInfo.messages[i].description);
            this.jobMessage = jobInfo.messages[i].description;
          }
        messageIndex = jobInfo.messages.length;
      }
    }
  }
  @action.bound async onClickSurface() {
    resetMap();
    designStore.hideCrossSectionToolbar = true;
    designStore.hideSingleCrossSection = true;
    padDesignStore.showLoader = true;

    padDesignStore.displayedPadsIds = [];

    this.selectedPadsInfo.forEach((pad) => {
      padDesignStore.displayedPadsIds.push(pad.designElementId)
    });

    const input_structure_json_ids: number[] = padDesignStore.displayedPadsIds.map((item) => {
      return item as number;
    });

    let i = 0;
    while (i < padDesignStore.padLayers.length) {
      const layer = padDesignStore.padLayers[i];
      const status = input_structure_json_ids.findIndex((id) => id.toString() === layer.id);
      if (status === -1) {
        padDesignStore.padLayers.splice(status, 1);
      } else {
        i++;
      }
    }

    try {
      if (0 < input_structure_json_ids.length) {
        await DesignContainerVM.loadMultiStructureSurface(input_structure_json_ids);
      } else {
        await DesignContainerVM.displayOriginalSurface();
      }
      padDesignStore.latestElementId = undefined;
      setStore.pointsOnMap = false;
      this.mapView!.ui.add(gisModel.locationWidget, 'bottom-left');
    } catch (error) {
      MessageCenter.addError((error as Error).message);
    } finally {
      setTimeout(() => {
        setStore.isLoading = false;
      }, 1000);
    }
    padDesignStore.showLoader = false;
  }

  @action.bound async getDesignElements() {
    padDesignStore.padSummaryTable = [];
    this.selectedPadsInfo = [];
    designStore.designElementId = 0;
    padDesignStore.showLoader = true;
    designStore.designElementTypeId = appConfig.hardCodes.designElementTypePad //To enable ToolBar on the map for Pad
    const sessionId = designStore.projectsStore.currentSessionId;
    if (!sessionId) {
      padDesignStore.showLoader = false;
      return;
    }
    const structureList = 'Pad';
    try {
      const response = await SETAPI.DesignElementController.getAllDesignElementsBySessionAndTypeUsingGET(
        sessionId,
        structureList
      );

      if (response && response.data) {
        let data: any = response.data;
        padDesignStore.padSummaryTable = []

        //pad structures
        data = data.filter(e => e.designElementTypeId === appConfig.hardCodes.designElementTypePad)
        data.map((item) => this.mapIdToName(item));
        data.map((item, index) => {
          item.checked = false;
        });

        padDesignStore.padSummaryTable.push(...data);

        // data.forEach((item, index) => {
        //   const selected = padDesignStore.selectedPadsInfo.find((pad) => {
        //     return pad.designElementId === item.designElementId;
        //   });
        //   if (selected) {
        //     const e = {
        //       target: {
        //         checked: true,
        //       },
        //     };
        //     this.handleCheckboxChange(e, index);
        //   }
        // })
        data.map((item, index) => {
          item.checked = false;
          if (padDesignStore.padLayers.some((layer) => layer.id === `${item.designElementId}`)) {
            const e = {
              target: {
                checked: true,
              },
            };
            this.handleCheckboxChange(e, index);
          }
        });

        padDesignStore.showLoader = false;
      }
    } catch (err) {
      padDesignStore.showLoader = false;
      console.error(err);
      this.error = true;
      this.errorMsg = `Uh oh - something went wrong. ${err}`;
    }
  }

  @action.bound mapIdToName(item) {
    switch (item.designElementTypeId) {
      case 14:
        item.designElementTypeId = {
          name: 'Pad',
          id: 14,
        };
        break;
      default:
        return;
    }
  }

  @action.bound parseAndConvert(data: DesignElementResponse): void {
    const parsedData = JSON.parse(data.json!);
    padDesignStore.newDesignForm = parsedData;
    padDesignStore.newPadResults = parsedData;
    padDesignStore.newDesignForm.designElementId = data.designElementId!;
  }

  @action.bound async openSinglePad(event, item) {
    const id = item.designElementId;
    try {
      const response = await SETAPI.DesignElementController.surveyDesignElementByIdUsingGET(id);
      console.warn('OPEN SINGLE PAD RESPONSE: ', response);
      const data: DesignElementResponse[] = response['data'];

      if (data[0] && this.mapView) {
        this.parseAndConvert(data[0]);
        padDesignStore.pendingEdits = { name: false, design: false, unsavedEdits: true };
        padDesignStore.hasDesign = true;
        padDesignStore.editDesign = true;
        padDesignStore.showLoader = true;
        padDesignStore.currentTab = 'New Pad';
        const index = padDesignStore.padSummaryTable.findIndex((pad) => {
          return pad.designElementId === id
        });
        if (index !== -1) {
          padDesignStore.padSummaryTable[index].checked = true;
          this.selectedPadsInfo.push(padDesignStore.padSummaryTable[index]);
        }
        const layerIndex = padDesignStore.padLayers.findIndex((layer) => layer.id === id.toString());
        if (layerIndex !== -1) {
          this.mapView.map.remove(padDesignStore.padLayers[layerIndex]);
          padDesignStore.padLayers.splice(layerIndex, 1);
        }
        padDesignStore.addPadToMap(padDesignStore.newPadResults, this.mapView, true, projectsLibraryStore.projectSpatialReference!, id);
        padDesignStore.originalDesignLayer = padDesignStore.currentDesignLayer;
        padDesignStore.originalPadResults = padDesignStore.newPadResults;
        padDesignStore.originalIdString = id;
        designStore.structureLayerExists = true;
      }
    } catch (err) {
      console.error(err);
    }
  }

  @action.bound handleCheckboxChange(e: any, index, updateArray?: boolean) {
    padDesignStore.padSummaryTable[index].checked = e.target.checked;
    if (e.target.checked) {
      this.selectedPadsInfo.push(padDesignStore.padSummaryTable[index]);
    } else {
      this.selectedPadsInfo.replace(
        this.selectedPadsInfo.filter((p, j) => {
          return !(p === padDesignStore.padSummaryTable[index]);
        })
      );
    }
    this.somePadSelected = this.selectedPadsInfo.length > 0 ? true : false;
    // if (updateArray) padDesignStore.selectedPadsInfo = this.selectedPadsInfo;
  }

  @action.bound displaySelectedPad() {
    padDesignStore.selectedPadsInfo = this.selectedPadsInfo;
    if (this.props.applyPadStructuresToMap) this.props.applyPadStructuresToMap();
  }

  @action.bound async deleteDesignElement(event, item) {
    padDesignStore.showLoader = true;
    const designElementId = item.designElementId;
    try {
      await SETAPI.DesignElementController.deleteDesignElementUsingPOST(designElementId);
      if (designElementId === padDesignStore.latestElementId) {
        await DesignContainerVM.displayOriginalSurface();
        padDesignStore.latestElementId = undefined;
      }
      console.warn('trying to find pad layer with id: ' + designElementId);
      const layerIndex = padDesignStore.padLayers.findIndex((layer) => layer.id === designElementId.toString());
      if (layerIndex !== -1) {
        if (this.mapView) this.mapView.map.remove(padDesignStore.padLayers[layerIndex]);
        padDesignStore.padLayers.splice(layerIndex, 1);
      }
      // removeLayers([padDesignStore.currentDesignLayer], this.mapView!);
      const layer = this.mapView!.map.findLayerById(designElementId.toString())
      if (layer) {
        this.mapView!.map.remove(layer)
      }
      const map = EngineeringMapService.instance?.mapView?.map;
      designStore.removeDesignedContours(map);
      await this.getDesignElements();

      // prepare displayed pads
      const index = padDesignStore.displayedPadsIds.indexOf(designElementId);
      if (index >= 0) {
        padDesignStore.displayedPadsIds.splice(index, 1);
      }

      const rest_structure_json_ids: number[] = padDesignStore.displayedPadsIds.map((id) => {
        return id;
      });
      if (0 < rest_structure_json_ids.length) {
        await DesignContainerVM.loadMultiStructureSurface(rest_structure_json_ids);
      }
      if (padDesignStore.padSummaryTable.length == 0) {
        await DesignContainerVM.displayOriginalSurface();
        padDesignStore.latestElementId = undefined;
        padDesignStore.displayedPadsIds = [];
      }
    } catch (e) {
      console.warn('deleteDesignElement error = ', e);
    } finally {
      if (this.mapView && !designStore.hideSingleCrossSection) await designStore.validateSingleCrossSection(this.mapView);
    }
  }
}

